import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  SimpleButton,
  LoadingButton,
  Input,
  TextArea,
  Select,
  FormDialog,
  Toast,
  NotificationsList,
  DropdownMenu,
  Modal,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsBoxArrowUpRight, BsTrash } from "react-icons/bs";
import { MdAdd } from "react-icons/md";
import { usePppApi } from "../services/pppApi";
import { useAuthUser } from "../hooks/useAuthUser";
import { usePppAiContext } from "../hooks/usePppAiContext";

const STATO_CFG = {
  bozza:      { cls: "bg-gray-100 text-gray-700" },
  pubblicato: { cls: "bg-green-100 text-green-700" },
  scaduto:    { cls: "bg-red-100 text-red-700" },
};

function StatoBadge({ stato, _locate }) {
  const cfg = STATO_CFG[stato] ?? { cls: "" };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
      {_locate(`ppp_avviso_stato_${stato}`) ?? stato}
    </span>
  );
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

export default function AvvisoPubblicoList() {
  const navigate = useNavigate();
  const { _locate, setOpenForm, addToast, addModal } = useStateContext();
  const { can, user, ready } = useAuthUser();
  usePppAiContext('ppp.avviso');
  const api = usePppApi();

  const isRup = user?.role === 'rup';

  const [avvisi, setAvvisi]           = useState([]);
  const [procedimenti, setProcedimenti] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [createErrors, setCreateErrors] = useState(null);
  const [entiRup, setEntiRup]         = useState([]);

  const [createForm, setCreateForm] = useState(null);

  useEffect(() => {
    api.listAvvisiPubblici({}, {
      onSuccess: (data) => { setAvvisi(data ?? []); setLoading(false); },
      onError:   () => setLoading(false),
    });
    api.listProcedimenti({ onSuccess: (data) => setProcedimenti(data ?? []) });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ready || !isRup) return;
    api.listMieiEnti({
      onSuccess: (data) => setEntiRup(data.filter((e) => e.stato === 'accettato')),
      onError: () => {},
    });
  }, [ready, isRup]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAvvisi = () => {
    setLoading(true);
    api.listAvvisiPubblici({}, {
      onSuccess: (data) => { setAvvisi(data ?? []); setLoading(false); },
      onError:   () => setLoading(false),
    });
  };

  const handleElimina = (row) => {
    addModal(
      <Modal
        title={`${_locate("ppp_avviso_elimina_titolo")}: ${row.oggetto}`}
        content={_locate("ppp_avviso_elimina_messaggio")}
        confirm={_locate("ppp_elimina")}
        style="danger"
        onClick={() => {
          api.eliminaAvvisoPubblico(row.id, {
            onSuccess: () => {
              addToast(<Toast duration={4} content={_locate("ppp_avviso_eliminato")} />);
              loadAvvisi();
            },
          });
        }}
      />
    );
  };

  const openCreate = () => {
    setCreateForm({
      enteId: "",
      oggetto: "",
      procedimentoId: "",
      descrizioneIntervento: "",
      requisitiMinimi: "",
      sopraSogliaEuropea: false,
      numeroAtto: "",
      note: "",
    });
    setOpenForm(true);
  };

  const setField = (field) => (e) => setCreateForm((f) => ({ ...f, [field]: e.target.value }));
  const setCheck = (field) => (e) => setCreateForm((f) => ({ ...f, [field]: e.target.checked }));

  const handleCrea = () => {
    if (!createForm?.oggetto) return;
    setSaving(true);
    setCreateErrors(null);
    const payload = {
      ...createForm,
      procedimentoId: createForm.procedimentoId ? parseInt(createForm.procedimentoId) : null,
      ...(isRup && createForm.enteId && !createForm.procedimentoId
        ? { enteId: parseInt(createForm.enteId, 10) }
        : {}),
    };
    api.creaAvvisoPubblico(payload, {
      onSuccess: (data) => {
        addToast(<Toast duration={4} content={_locate("ppp_avviso_creato")} />);
        setSaving(false);
        setOpenForm(false);
        navigate(`/ppp/avvisi-pubblici/${data.id}`);
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setCreateErrors(response.errors); },
    });
  };

  const cols = [
    { name: "action",            label: "",                                        size: "w-10 max-w-[40px]", orderable: 0 },
    { name: "oggetto",           label: _locate("ppp_avviso_oggetto") },
    { name: "stato",             label: _locate("ppp_avviso_stato"),              size: "w-28" },
    { name: "procedimento",      label: _locate("ppp_avviso_procedimento") },
    { name: "dataPubblicazione", label: _locate("ppp_avviso_data_pubblicazione"), size: "w-36" },
    { name: "termine",           label: _locate("ppp_avviso_termine_proposte"),   size: "w-36" },
  ];

  const cells_style = {
    action: (row) => (
      <DropdownMenu
        menu_items={[
          <button
            key="apri"
            onClick={() => navigate(`/ppp/avvisi-pubblici/${row.id}`)}
            className="w-full flex items-center gap-4 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
          >
            <BsBoxArrowUpRight />
            <span>{_locate("ppp_apri")}</span>
          </button>,
          <button
            key="elimina"
            onClick={() => handleElimina(row)}
            className="w-full flex items-center gap-4 px-4 py-2 text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600"
          >
            <BsTrash />
            <span>{_locate("ppp_elimina")}</span>
          </button>,
        ]}
      />
    ),
    oggetto:           (row) => (
      <div className="cursor-pointer" onClick={() => navigate(`/ppp/avvisi-pubblici/${row.id}`)}>
        <span className="font-medium text-gray-900 dark:text-white hover:text-gray-400 dark:hover:text-[#a9a68e]">{row.oggetto}</span>
      </div>
    ),
    stato:             (row) => <StatoBadge stato={row.stato} _locate={_locate} />,
    procedimento:      (row) => row.procedimentoTitolo ?? "—",
    dataPubblicazione: (row) => formatDate(row.dataPubblicazione),
    termine:           (row) => formatDate(row.terminePresentazioneProposte),
  };

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {_locate("ppp_avviso_titolo_lista")}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{_locate("ppp_avviso_titolo_sottotitolo")}</p>
        </div>
        {can('ppp.avviso.create') && (
          <SimpleButton customFunc={openCreate} text={<span className="flex items-center gap-1"><MdAdd size={16} />{_locate("ppp_avviso_nuovo")}</span>} />
        )}
      </div>

      <Table
        dataset={avvisi}
        cols={cols}
        cells_style={cells_style}
        loading={loading}
      />
      </div>

      {createForm && (
        <FormDialog title={_locate("ppp_avviso_nuovo_titolo")} size="w-[640px]">
          <div className="space-y-4">
            <Input
              label={_locate("ppp_avviso_oggetto")}
              required
              value={createForm.oggetto}
              onChange={setField("oggetto")}
              placeholder={_locate("ppp_avviso_oggetto_hint")}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {_locate("ppp_avviso_procedimento")}
              </label>
              <Select
                value={createForm.procedimentoId}
                onChange={(e) => setCreateForm((f) => ({ ...f, procedimentoId: e.target.value, enteId: "" }))}
                options={[
                  { value: "", label: _locate("ppp_avviso_procedimento_placeholder") },
                  ...(procedimenti ?? []).map((p) => ({ value: p.id, label: p.titolo })),
                ]}
              />
            </div>

            {isRup && !createForm.procedimentoId && (
              <Select
                label={_locate("ppp_prog_form_ente") || "Ente concedente"}
                required
                value={createForm.enteId}
                onChange={(e) => setCreateForm((f) => ({ ...f, enteId: e.target.value }))}
                options={entiRup.map((en) => ({ value: String(en.enteId), label: en.denominazione + (en.comune ? ` (${en.comune})` : "") }))}
              />
            )}

            <TextArea
              label={_locate("ppp_avviso_descrizione_intervento")}
              rows={4}
              value={createForm.descrizioneIntervento}
              onChange={setField("descrizioneIntervento")}
              placeholder={_locate("ppp_avviso_descrizione_hint")}
            />

            <TextArea
              label={_locate("ppp_avviso_requisiti_minimi")}
              rows={4}
              value={createForm.requisitiMinimi}
              onChange={setField("requisitiMinimi")}
              placeholder={_locate("ppp_avviso_requisiti_hint")}
            />

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sopraSogliaEuropeaCreate"
                checked={createForm.sopraSogliaEuropea}
                onChange={setCheck("sopraSogliaEuropea")}
                className="w-4 h-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="sopraSogliaEuropeaCreate" className="text-sm text-gray-700 dark:text-gray-300">
                {_locate("ppp_avviso_sopra_soglia_europea")}
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input label={_locate("ppp_avviso_numero_atto")} value={createForm.numeroAtto} onChange={setField("numeroAtto")} placeholder="Es. Det. Dir. n. 42/2026" />
            </div>

            <TextArea label={_locate("ppp_avviso_note")} rows={2} value={createForm.note} onChange={setField("note")} />

            <NotificationsList errors={createErrors} />

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <SimpleButton customFunc={() => setOpenForm(false)} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={createForm.oggetto ? handleCrea : undefined}
                text={_locate("ppp_avviso_salva")}
              />
            </div>
          </div>
        </FormDialog>
      )}
    </div>
  );
}
